import { P2GBox } from '@parcel2go/core-ui';
import { P2GButton } from '@parcel2go/core-ui';
import { P2GText } from '@parcel2go/core-ui';

import { useEffect } from 'react';
import { useState } from 'react';
import {
  ConsignmentParcelApiModel,
  Dimensions,
  PackageApiModel,
  SmartSendItemApiModel
} from '~/lib/api/smartsend/types';
import { SumNumber } from '../../../lib/helpers/ArrayHelper';
import { uid } from '../../../lib/helpers/uid';
import { DimensionsSelection } from '../DimensionsSelection';
import { SmartSendItemGroup } from '../item/SmartSendGroup';
import SidePanelSection, { SidePanelSections } from '../../../../../components/sidepanel-section';
import { FilterToSendableItems } from '../panels/FilterToSendableItems';
import { GenerateCombiningSummary } from './GroupingHelper';
import { GroupingPanelParcels } from './GroupingPanelParcels';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';

export interface props {
  group: SmartSendItemGroup;
  getPackages: () => Promise<PackageApiModel[]>;
  create: (items: SmartSendItemApiModel[]) => void;
}

export const Combining: React.FC<props> = (props) => {
  const [dims, setDims] = useState<Dimensions>({
    Height: 0,
    Width: 0,
    Length: 0,
    Weight: SumNumber(props.group.Items.map((x) => x.PackageDimensions.Weight || 0))
  });

  const [parcel, setParcel] = useState<ConsignmentParcelApiModel>({
    UID: uid(),
    Id: null,
    Contents: [],
    Dimensions: dims,
    CanAdd: true
  });
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  useEffect(() => {
    const newParcel: ConsignmentParcelApiModel = {
      UID: uid(),
      Id: null,
      Contents: [],
      Dimensions: dims,
      CanAdd: true
    };
    props.group.Items.forEach((i) => {
      i.MarketplaceOrderLines.filter(FilterToSendableItems).forEach((m) => {
        newParcel.Contents.push({ ...m, UID: uid(), Description: m.Product.Description });
      });
    });
    setParcel(newParcel);
  }, [props.group.Items.length]);

  function combine() {
    const summary = GenerateCombiningSummary(props.group, dims);
    if (summary) {
      props.create(summary.items);
    }
  }

  const currencyCode = props.group.Items[0]?.CurrencyCode;

  return (
    <>
      <SidePanelSections>
        <P2GBox>
          <P2GText>
            <Trans
              t={t}
              i18nKey="SEND_TOGETHER_INTRO_TEXT"
              defaults={`Your customer has purchased multiple items from you. 
                <br /> Would you like to send all of these orders in one parcel to save postage costs? <br /> This will include:`}
            />
          </P2GText>
        </P2GBox>

        <SidePanelSection title={t('SHIPMENT_SUMMARY_SUBTITLE', { defaultValue: 'Shipment Summary' })}>
          <GroupingPanelParcels parcels={[parcel]} currencyCode={currencyCode} />
        </SidePanelSection>

        <SidePanelSection
          title={t('ADDITIONAL_SHIPMENT_DIMENSION_SUBTITLE', { defaultValue: 'Additional Shipment Dimensions' })}
        >
          <DimensionsSelection
            startMinimised={false}
            dimensions={dims}
            fallback={props.group.Items[0].PackageDimensions}
            onUpdate={setDims}
            getPackages={props.getPackages}
          />
        </SidePanelSection>
      </SidePanelSections>

      <P2GBox mt={2}>
        <P2GButton disabled={props.group.Items.length <= 1} onClick={combine}>
          {t('SEND_TOGETHER_BUTTON', { defaultValue: 'Send together' })}
        </P2GButton>
      </P2GBox>
    </>
  );
};
